import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useDelayedExecution } from 'hooks/useDelayedExecution';
import { GET_NEW_TOKEN_BEFORE_EXPIRATION_MINUTES } from '.';
import { addSeconds } from 'date-fns';
import config from 'config';
import { logError } from 'util/logging';
import { calculateWhenToGetNewToken } from 'auth/tokenSchedule';
import { AuthFailure, backoffSeconds, classifyAuthError, MAX_TOKEN_REFRESH_ATTEMPTS } from 'auth/authErrors';
import { useSessionExpired } from 'auth/SessionExpiredContext';
import { useAccessTokenReady } from 'auth/AccessTokenContext';

export type AccessTokenStatus =
  | { status: 'loading' }
  | { status: 'ready' }
  | { status: 'failed'; failure: AuthFailure };

// Since we are setting the token in the global axios configuration, we have to set it and renew it explicitly here
// This hook should only be used at one level in the component tree, as multiple instantiating will result in redunant calls to get the token
// Should it be necessary to use it in multiple places, it could be refactored to use a context or similar
export function useConfigureAccessToken() {
  const { getAccessTokenSilently } = useAuth0();
  const [accessTokenState, setAccessTokenState] = useState<AccessTokenStatus>({ status: 'loading' });
  const [getNewTokenAt, setGetNewTokenAt] = useState<Date | undefined>(undefined);
  const attemptsRef = useRef(0);
  const { reportSessionExpired } = useSessionExpired();
  const { setAccessTokenReady } = useAccessTokenReady();

  const applyToken = (token: string) => {
    attemptsRef.current = 0;
    setGetNewTokenAt(calculateWhenToGetNewToken(token, GET_NEW_TOKEN_BEFORE_EXPIRATION_MINUTES));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setAccessTokenState({ status: 'ready' });
    // Routes gates its initial page-view tracking on this — the analytics POST reads the header
    // we just set.
    setAccessTokenReady(true);
  };

  // Decide whether to back off and try again, or to stop and hand the user a way out.
  //
  // The old code did neither — it logged and left getNewTokenAt untouched, so the schedule stayed
  // pinned in the past and re-fired on every tab focus for the life of the tab.
  const handleFailure = (error: any) => {
    const failure = classifyAuthError(error, navigator.onLine);
    const attempt = attemptsRef.current;
    const canRetry = failure.kind !== 'terminal' && attempt < MAX_TOKEN_REFRESH_ATTEMPTS;

    if (canRetry) {
      attemptsRef.current = attempt + 1;
      setGetNewTokenAt(addSeconds(new Date(), backoffSeconds(attempt)));
      return;
    }

    // Cancel the schedule outright — useDelayedExecution returns early on undefined.
    setGetNewTokenAt(undefined);

    // Once the app is on screen it stays mounted, so unsaved work isn't thrown away; the dialog (or
    // the 401s that follow) gives the user a way back in. Before that there is nothing to protect,
    // so AuthenticatedRoutes swaps the loading screen for SessionExpiredScreen.
    const isAppShowing = accessTokenState.status === 'ready';
    if (!isAppShowing) {
      setAccessTokenState({ status: 'failed', failure });
    }

    if (failure.kind === 'terminal') {
      // Expected and user-recoverable, so SessionExpiredProvider logs a warning rather than an error.
      reportSessionExpired(isAppShowing ? 'token-refresh' : 'token-configure');
      return;
    }

    // Exhausted retries on something we couldn't classify — that is worth an error.
    logError(error, `Failed to configure access token for axios`, {
      errorType: failure.code,
      errorDescription: failure.description,
      attempts: attempt + 1,
      pathname: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
    });
  };

  const fetchToken = async () => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: config.AUTH0_API_IDENTIFIER,
        },
      });
      applyToken(token);
    } catch (error: any) {
      handleFailure(error);
    }
  };

  useEffect(() => {
    fetchToken();
  }, []);

  // Refresh the token X minutes before it expires, and retry with backoff after a transient failure
  useDelayedExecution(fetchToken, getNewTokenAt);

  return {
    accessTokenState,
    isAccessTokenReady: accessTokenState.status === 'ready',
  };
}
